/**
 * dashboard/reports/history.tsx — Tutor sent reports history (N-18).
 *
 * Lists the tutor's recent reports from sentReportsQuery. Each row
 * shows student name, subject, delivery status, and a relative date.
 */

import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import { freshSessionQuery, sentReportsQuery } from '../../../lib/api/queries.js';

export const Route = createFileRoute('/dashboard/reports/history')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'admin') {
      throw redirect({ to: '/admin' });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: ReportHistoryPage,
});

function ReportHistoryPage(): ReactNode {
  const reports = useQuery(sentReportsQuery);
  const list = reports.data?.ok ? reports.data.data.reports : [];

  return (
    <main className="page-shell" data-testid="report-history-page">
      <PageHeader
        eyebrow="Riwayat rapor"
        title="Rapor yang sudah terkirim"
        description="Daftar rapor progres yang pernah Anda kirim ke orang tua."
      />

      {reports.isLoading ? (
        <p>Memuat riwayat…</p>
      ) : list.length === 0 ? (
        <StatePanel
          variant="empty"
          title="Belum ada rapor terkirim"
          description="Rapormu akan muncul di sini setelah Anda mengirim rapor pertama."
        />
      ) : (
        <table className="data-table" data-testid="report-history-table">
          <thead>
            <tr>
              <th>Tanggal</th>
              <th>Siswa</th>
              <th>Subjek</th>
              <th>Status</th>
              <th>Isi (excerpt)</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.reportId} data-testid={`report-row-${r.reportId}`}>
                <td>{new Date(r.sentAt).toLocaleString('id-ID')}</td>
                <td>{r.studentId}</td>
                <td>{r.subject}</td>
                <td>
                  <span className={`status-pill is-${r.deliveryStatus}`}>
                    {r.deliveryStatus}
                  </span>
                </td>
                <td>{r.body.slice(0, 80)}{r.body.length > 80 ? '…' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}